import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';

import { LoadingSpinner } from '../../core/LoadingSpinner';
import {
  useGetTabletDevices,
  useUnpairTabletDevice,
} from '../../hooks/reactQuerys/TabletQueries';
import { ITabletDevice } from '../../Interfaces/ITabletDevice';
import { PairTabletDialog } from './PairTabletDialog';

const ONLINE_THRESHOLD_MINUTES = 2;

function isOnline(lastSeen?: string | null): boolean {
  if (!lastSeen) return false;
  const ts = new Date(lastSeen).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= ONLINE_THRESHOLD_MINUTES * 60_000;
}

function formatDate(value?: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE');
}

export const TabletsView: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { tabletDevices, isLoadingTabletDevices, refetchTabletDevices } =
    useGetTabletDevices();
  const { unpairTabletDevice, isPendingUnpairTabletDevice } =
    useUnpairTabletDevice();

  if (isLoadingTabletDevices) {
    return <LoadingSpinner />;
  }

  const devices: ITabletDevice[] = tabletDevices ?? [];

  async function handleUnpair(device: ITabletDevice) {
    if (!window.confirm(`Tablet "${device.Name}" wirklich entkoppeln?`)) return;
    try {
      await unpairTabletDevice(device.idTabletDevice);
    } catch (e) {
      window.alert('Entkoppeln fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        mb={2}
      >
        <Box>
          <Typography variant='h5'>Tablets</Typography>
          <Typography variant='body2' color='text.secondary'>
            Mandanten-Tablets, die mit dieser Kanzlei verbunden sind.
          </Typography>
        </Box>
        <Stack direction='row' spacing={1}>
          <Tooltip title='Neu laden'>
            <IconButton onClick={() => refetchTabletDevices()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Tablet koppeln
          </Button>
        </Stack>
      </Stack>

      {devices.length === 0 && (
        <Alert severity='info'>
          Es ist noch kein Tablet gekoppelt. Starten Sie die DiNo-Tablet-App auf
          dem Endgerät und klicken Sie auf „Tablet koppeln".
        </Alert>
      )}

      {devices.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Letzte Aktivität</TableCell>
              <TableCell>Gekoppelt am</TableCell>
              <TableCell>Gekoppelt von</TableCell>
              <TableCell align='right'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map(d => (
              <TableRow key={d.idTabletDevice} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {d.Name}
                  </Typography>
                  {d.Description && (
                    <Typography variant='caption' color='text.secondary'>
                      {d.Description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {isOnline(d.LastSeen_at) ? (
                    <Chip label='Online' color='success' size='small' />
                  ) : (
                    <Chip label='Offline' size='small' />
                  )}
                </TableCell>
                <TableCell>{formatDate(d.LastSeen_at)}</TableCell>
                <TableCell>{formatDate(d.Created_at)}</TableCell>
                <TableCell>
                  {[d.PairedByFirstName, d.PairedByLastName]
                    .filter(Boolean)
                    .join(' ') || '–'}
                </TableCell>
                <TableCell align='right'>
                  <Tooltip title='Entkoppeln'>
                    <span>
                      <IconButton
                        onClick={() => handleUnpair(d)}
                        disabled={isPendingUnpairTabletDevice}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PairTabletDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
};
